import { computed, reactive, inject } from 'vue';
import { useRoute } from 'vue-router';
import _ from 'lodash';
import store from '@/store';
import {
  TRANSACTION_MODE_OPTIONS,
  REMUNERATION_TYPE_OPTIONS,
  AREA_TYPE_OPTIONS,
  PURPOSE_OPTIONS,
  QUICK_GROSS_OPTIONS,
  PRICE_RECOMMEND_OPTIONS,
  BUILDING_AREA_TYPE_RECOMMEND_OPTIONS,
  TYPE_RECOMMEND_OPTIONS,
  START_DATE_OPTIONS,
  PARKING_SLOT_OPTIONS,
  FORM_PROPERTIES,
} from '@/constants/estate';
import { DEFAULT_DATE_FORMAT } from '@/constants/common';

export default function () {
  const $formatTime = inject('$formatTime');
  const cities = computed(() => store.getters['common/cityList'].cities);
  const districts = computed(() => store.getters['common/districtList'].districts);
  const trainLines = computed(() => store.getters['estate/trainLineStation']);
  const route = useRoute();

  const searchFromHome = computed(() => route.query?.from && route.query?.from?.includes('home'));

  const formatForSelectBox = (data) => data.map((item) => {
    item.value = parseInt(item.code_id, 10);
    item.label = item.code_name;
    return item;
  });

  const formatForCheckBox = (data) => data.map((item) => {
    item.checked = false;
    item.title = item.code_name;
    return item;
  });

  const priceFromOptions = computed(() => formatForSelectBox(store.getters['common/priceFromOptions']));

  const priceToOptions = computed(() => formatForSelectBox(store.getters['common/priceToOptions']));

  const timeToStationOptions = computed(() => formatForSelectBox(store.getters['common/timeToStationOptions']));

  const timeFromStationOptions = computed(() => formatForSelectBox(store.getters['common/timeFromStationOptions']));

  const landOwnershipOptions = computed(() => formatForSelectBox(store.getters['common/landOwnershipOptions']));

  const situationOptions = computed(() => formatForSelectBox(store.getters['common/situationOptions']));

  const constructAreaFromOptions = computed(() => formatForSelectBox(store.getters['common/constructAreaFromOptions']));

  const constructAreaToOptions = computed(() => formatForSelectBox(store.getters['common/constructAreaToOptions']));

  const constructDateFromOptions = computed(() => formatForSelectBox(store.getters['common/constructDateFromOptions']));

  const constructDateToOptions = computed(() => formatForSelectBox(store.getters['common/constructDateToOptions']));

  const totalFlatFromOptions = computed(() => formatForSelectBox(store.getters['common/totalFlatFromOptions']));

  const totalFlatToOptions = computed(() => formatForSelectBox(store.getters['common/totalFlatToOptions']));

  const typeOptions = computed(() => formatForCheckBox(store.getters['common/typeOptions']));

  const structureOptions = computed(() => formatForCheckBox(store.getters['common/structureOptions']));

  const buildingMapOptions = computed(() => formatForCheckBox(store.getters['common/buildingMapOptions']));

  const prefectures = computed(() => store.getters['common/prefectureCategories']);

  const transactionModeOptions = reactive(TRANSACTION_MODE_OPTIONS);
  const remunerationTypeOptions = reactive(REMUNERATION_TYPE_OPTIONS);
  const areaTypeOptions = reactive(AREA_TYPE_OPTIONS);
  const purposesOptions = reactive(PURPOSE_OPTIONS);
  const quickGrossOptions = reactive(QUICK_GROSS_OPTIONS);
  const startDateOptions = reactive(START_DATE_OPTIONS);
  const priceRecommendOptions = reactive(PRICE_RECOMMEND_OPTIONS);
  const buildingAreaTypeRecommendOptions = reactive(BUILDING_AREA_TYPE_RECOMMEND_OPTIONS);
  const typeRecommendOptions = reactive(TYPE_RECOMMEND_OPTIONS);
  const parkingSlotOptions = reactive(PARKING_SLOT_OPTIONS);
  const formProperties = reactive(_.cloneDeep(FORM_PROPERTIES));

  const chooseRecommend = (value, property) => {
    formProperties[`${property}_from`] = value.from;
    formProperties[`${property}_to`] = value.to;
  };

  const onlyUnique = (value, index, self) => self.indexOf(value) === index;

  const sortAsc = (a, b) => parseInt(a, 10) - parseInt(b, 10);

  const arrInArr = (parentArr, childrenArr) => childrenArr.every((item) => parentArr.includes(item));

  const chooseGroup = (option, property, arrayTarget) => {
    const correctData = arrayTarget.filter((item) => item?.biko1?.includes(option.biko));
    const correctDataCodeIds = correctData.map((item) => item.code_id).sort(sortAsc);
    if (arrInArr(formProperties[property], correctDataCodeIds)) {
      correctDataCodeIds.forEach((item) => {
        formProperties[property].splice(formProperties[property].indexOf(item), 1);
      });
    } else {
      let newValue = [...formProperties[property], ...correctDataCodeIds];
      newValue = newValue.filter(onlyUnique);
      formProperties[property] = newValue;
    }

    const checkedAll = correctData?.every((item) => item.checked);
    arrayTarget.forEach((item) => {
      if (item?.biko1?.includes(option.biko)) {
        if (checkedAll) {
          item.checked = false;
          option.selected = false;
        } else {
          item.checked = option.selected;
        }
      }
    });
  };

  const titleCase = (str) => {
    // eslint-disable-next-line
    let splitStr = str.toLowerCase().split('_');
    for (let i = 0; i < splitStr.length; i++) {
      splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
    }
    return splitStr.join('');
  };

  const getLocationNames = (dataQuery, type = 'district') => {
    let arrSelected = dataQuery.districts;
    let arrTarget = districts.value;
    if (type === 'city') {
      arrSelected = dataQuery.cities;
      arrTarget = cities.value;
    }
    if (arrSelected && arrTarget) {
      const arrCorrect = arrTarget.filter((item) => arrSelected.includes(item.government_code));
      return arrCorrect.map((item) => item.city_name);
    }
    return '';
  };

  const getTrainLineNames = () => trainLines.value.map((item) => item.line_name);

  const getStationNames = (dataQuery) => {
    let allStations = [];
    trainLines.value.forEach((item) => {
      allStations = [...allStations, ...item.stations];
    });

    return allStations
      .filter((item) => dataQuery.stations?.includes(item.station_code))
      .map((item) => item.station_name)
      .filter(onlyUnique);
  };

  const findItemByValue = (arrTarget, value) => arrTarget?.find((item) => item.value === parseInt(value, 10));

  const getDataFromQuery = (dataQuery) => {
    let priceFrom = dataQuery.price_from ? dataQuery.price_from : null;
    let priceTo = dataQuery.price_to ? dataQuery.price_to : null;
    let constructAreaFrom = dataQuery.construct_area_from ? dataQuery.construct_area_from : null;
    let constructAreaTo = dataQuery.construct_area_to ? dataQuery.construct_area_to : null;
    let transportTimeFrom = dataQuery.transport_time_from ? dataQuery.transport_time_from : null;
    let transportTimeTo = dataQuery.transport_time_to ? dataQuery.transport_time_to : null;
    let constructDateFrom = dataQuery.construct_date_from ? dataQuery.construct_date_from : null;
    let constructDateTo = dataQuery.construct_date_to ? dataQuery.construct_date_to : null;
    let totalFlatFrom = dataQuery.total_flat_from ? dataQuery.total_flat_from : null;
    let totalFlatTo = dataQuery.total_flat_to ? dataQuery.total_flat_to : null;

    if (priceFrom) {
      const itemSelected = findItemByValue(priceFromOptions.value, priceFrom);
      priceFrom = itemSelected ? parseFloat(itemSelected.biko1?.replace(/,/g, '')) : null;
    }

    if (priceTo) {
      const itemSelected = findItemByValue(priceToOptions.value, priceTo);
      priceTo = itemSelected ? parseFloat(itemSelected.biko1?.replace(/,/g, '')) : null;
    }

    if (constructAreaFrom) {
      const itemSelected = findItemByValue(constructAreaFromOptions.value, constructAreaFrom);
      constructAreaFrom = itemSelected ? parseFloat(itemSelected.biko1?.replace(/,/g, '')) : null;
    }

    if (constructAreaTo) {
      const itemSelected = findItemByValue(constructAreaToOptions.value, constructAreaTo);
      constructAreaTo = itemSelected ? parseFloat(itemSelected.biko1?.replace(/,/g, '')) : null;
    }

    if (transportTimeFrom) {
      const itemSelected = findItemByValue(timeFromStationOptions.value, transportTimeFrom);

      transportTimeFrom = itemSelected ? parseFloat(itemSelected.biko1) : null;
    }

    if (transportTimeTo) {
      const itemSelected = findItemByValue(timeToStationOptions.value, transportTimeTo);
      transportTimeTo = itemSelected ? parseFloat(itemSelected.biko1) : null;
    }

    if (constructDateFrom) {
      const itemSelected = findItemByValue(constructDateFromOptions.value, constructDateFrom);
      constructDateFrom = itemSelected ? itemSelected.biko1 : null;
    }

    if (constructDateTo) {
      const itemSelected = findItemByValue(constructDateToOptions.value, constructDateTo);
      constructDateTo = itemSelected ? itemSelected.biko1 : null;
    }

    if (totalFlatFrom) {
      const itemSelected = findItemByValue(totalFlatFromOptions.value, totalFlatFrom);
      totalFlatFrom = itemSelected ? itemSelected.biko1 : null;
    }

    if (totalFlatTo) {
      const itemSelected = findItemByValue(totalFlatToOptions.value, totalFlatTo);
      totalFlatTo = itemSelected ? itemSelected.biko1 : null;
    }

    if (searchFromHome.value) {
      dataQuery.from = route.query.from.replace('home-', '');
      dataQuery.prefecture_code = route.query.prefecture_code;
    }

    return {
      ...dataQuery,
      city: getLocationNames(dataQuery, 'city'),
      district: getLocationNames(dataQuery),
      // train_line: getTrainLineNames(dataQuery),
      station: getStationNames(dataQuery),
      transport_method:
        dataQuery.transport_method === 'true' || dataQuery.transport_method === true ? [2, 3] : [1],
      shortest_delivery_date_from: $formatTime(
        dataQuery.shortest_delivery_date_from,
        DEFAULT_DATE_FORMAT
      ),
      shortest_delivery_date_to: $formatTime(
        dataQuery.shortest_delivery_date_to,
        DEFAULT_DATE_FORMAT
      ),
      building_map:
        typeof dataQuery.building_map === 'string'
          ? dataQuery.building_map?.split(',')
          : dataQuery.building_map,
      structure:
        typeof dataQuery.structure === 'string'
          ? dataQuery.structure?.split(',')
          : dataQuery.structure,
      type: typeof dataQuery.type === 'string' ? dataQuery.type?.split(',') : dataQuery.type,
      price_from: priceFrom,
      price_to: priceTo,
      construct_area_from: constructAreaFrom,
      construct_area_to: constructAreaTo,
      transport_time_from: transportTimeFrom,
      transport_time_to: transportTimeTo,
      total_flat_from: totalFlatFrom,
      total_flat_to: totalFlatTo,
      construct_date_from: constructDateFrom,
      construct_date_to: constructDateTo,
    };
  };

  const removeNullOrDefaultPropertyObject = (objectData) => {
    Object.keys(objectData).forEach((property) => {
      if (
        // eslint-disable-next-line
        objectData[property] === '0' ||
        // eslint-disable-next-line
        !objectData[property] ||
        // eslint-disable-next-line
        (Array.isArray(objectData[property]) &&
          (objectData[property].length === 0 || objectData[property][0] === ''))
      ) {
        delete objectData[property];
      }
    });

    if (objectData.districts) {
      delete objectData.districts;
    }

    if (objectData.cities) {
      delete objectData.cities;
    }

    if (objectData.stations) {
      delete objectData.stations;
    }

    return objectData;
  };

  return {
    formatForSelectBox,
    formatForCheckBox,
    chooseRecommend,
    chooseGroup,
    onlyUnique,
    titleCase,
    getDataFromQuery,
    removeNullOrDefaultPropertyObject,
    formProperties,
    priceFromOptions,
    priceToOptions,
    timeToStationOptions,
    timeFromStationOptions,
    transactionModeOptions,
    remunerationTypeOptions,
    areaTypeOptions,
    purposesOptions,
    quickGrossOptions,
    landOwnershipOptions,
    situationOptions,
    constructAreaFromOptions,
    constructAreaToOptions,
    constructDateFromOptions,
    constructDateToOptions,
    totalFlatFromOptions,
    totalFlatToOptions,
    typeOptions,
    structureOptions,
    buildingMapOptions,
    startDateOptions,
    priceRecommendOptions,
    buildingAreaTypeRecommendOptions,
    typeRecommendOptions,
    parkingSlotOptions,
    prefectures,
    searchFromHome,
  };
}
